import { useMemo } from 'react'
import { KeplerTableField, MapDatasetEnriched, TableWidget } from 'features/builder'
import { MapType } from 'features/maps/types'
import { ALL_FIELD_TYPES } from '@carto/kepler.gl/constants'
import { getKeplerFieldFromDatasetScheme, isStaticSpatialIndexDataset } from 'features/builder/state/builderSliceUtils'
import { WIDGET_FIELDS_DENYLIST } from 'features/builder/ui/builderConstants'

function isFieldAllowed(field: KeplerTableField) {
  return field.type !== ALL_FIELD_TYPES.geojson && !field.artificial
}

export function isTableWidgetValid(widget: TableWidget, { source }: { source: MapDatasetEnriched }): boolean {
  return widget.columns.every((column) => source.fields?.some((f) => f.id === column.field))
}

function useWidgetTableModel({ source }) {
  const allowedFields: KeplerTableField[] = useMemo(() => {
    const result = [...source.fields].filter((f) => !WIDGET_FIELDS_DENYLIST.has(f.id))

    if (isStaticSpatialIndexDataset(source)) {
      result.unshift(getKeplerFieldFromDatasetScheme(source))
    }

    return result.filter(isFieldAllowed)
  }, [source])

  const initialFields = useMemo(() => {
    const fields = allowedFields.slice(0, 3)

    return fields.map((field) => ({ field: field.name }))
  }, [allowedFields])

  const validateWidget = (widget: TableWidget) => {
    widget.isValid = true

    if (widget.columns) {
      widget.columns = widget.columns.filter(({ field }) => {
        const sourceField = allowedFields.find((f) => f.name === field)
        return sourceField && isFieldAllowed(sourceField)
      })
    }

    if (!widget.columns || widget.columns.length === 0) {
      widget.isValid = false
    }
  }

  const isRaster = source.metadata?.sourceType === MapType.raster
  const isWidgetAvailable = !isRaster && allowedFields.length > 0
  const unavailableTooltipId = !isWidgetAvailable && isRaster ? 'builder.widgets.notAvailableTooltipRaster' : undefined

  return {
    validateWidget,
    isWidgetAvailable,
    allowedFields,
    initialFields,
    unavailableTooltipId
  }
}

export default useWidgetTableModel
